import { pxToEm } from 'util/pxToEm';
import { Box, Typography, useTheme } from '@mui/material';
import { AddIcon, DeleteIcon, EditSquareIcon } from 'components/icons';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSchoolsApi } from 'services/api';
import { ManageLearningOutcomesModal } from 'components/modals/ManageLearningOutcomesModal';
import { useSnackbar } from 'notistack';
import { Button, ConfirmationModal } from 'components/ui';
import { colorMediumGrey } from 'customColors';

interface LearningOutcome {
  id: string;
  title: string;
  description: string;
  naceCompetencies: string[];
}

interface LearningOutcomeWithExpanded extends LearningOutcome {
  isExpanded?: boolean;
}

export interface LearningOutcomesTabProps {
  schoolId: string;
}

export const LearningOutcomesTab = ({
  schoolId,
}: LearningOutcomesTabProps): ReactElement => {
  const theme = useTheme();
  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();

  const [learningOutcomes, setLearningOutcomes] = useState<
    LearningOutcomeWithExpanded[]
  >([]);
  const [
    isManageLearningOutcomesModalOpen,
    setIsManageLearningOutcomesModalOpen,
  ] = useState(false);
  const [editingOutcome, setEditingOutcome] = useState<LearningOutcome | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [
    learningOutcomeToDelete,
    setLearningOutcomeToDelete,
  ] = useState<LearningOutcome | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getLearningOutcomesList = useCallback(
    async (pageNum = 1) => {
      setIsLoading(true);
      try {
        const result = await schoolsApi.getLearningOutcomes(schoolId, {
          page: pageNum,
          pageSize: 20,
        });

        if (result.success) {
          const outcomesWithExpanded = result.value.learningOutcomes.map(
            outcome => ({
              ...outcome,
              isExpanded: false,
            }),
          );

          if (pageNum === 1) setLearningOutcomes(outcomesWithExpanded);
          else setLearningOutcomes(prev => [...prev, ...outcomesWithExpanded]);

          setHasMore(result.value.learningOutcomes.length === 20);
        } else {
          throw new Error(result.error.message);
        }
      } catch (error) {
        enqueueSnackbar('Error fetching learning outcomes:', {
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [schoolId, schoolsApi],
  );

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    void getLearningOutcomesList(1);
  }, [getLearningOutcomesList]);

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      void getLearningOutcomesList(nextPage);
    }
  }, [isLoading, hasMore, page, getLearningOutcomesList]);

  const handleDeleteLearningOutcome = (learningOutcome: LearningOutcome) => {
    setLearningOutcomeToDelete(learningOutcome);
    setShowDeleteConfirmation(true);
  };

  const handleEditLearningOutcome = (learningOutcome: LearningOutcome) => {
    setEditingOutcome(learningOutcome);
    setIsManageLearningOutcomesModalOpen(true);
  };

  const confirmDeleteLearningOutcome = async () => {
    if (!learningOutcomeToDelete) return;

    setIsDeleting(true);
    try {
      const result = await schoolsApi.deleteLearningOutcome(
        learningOutcomeToDelete.id,
      );
      if (result.success) {
        setLearningOutcomes(prev =>
          prev.filter(lo => lo.id !== learningOutcomeToDelete.id),
        );
        enqueueSnackbar('Learning outcome archived', {
          variant: 'success',
        });
      } else {
        throw new Error(result.error.message);
      }
    } catch (error) {
      enqueueSnackbar('Error archiving learning outcome', { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
      setLearningOutcomeToDelete(null);
    }
  };

  const toggleDescription = (learningOutcomeId: string) => {
    setLearningOutcomes(prev =>
      prev.map(lo =>
        lo.id === learningOutcomeId
          ? { ...lo, isExpanded: !lo.isExpanded }
          : lo,
      ),
    );
  };

  const truncateText = (text: string, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box maxWidth={820}>
      <Box display="flex" flexDirection="column" gap={8}>
        <Typography sx={{ fontWeight: 500, fontSize: { xs: 24, md: 32 }, letterSpacing: '-0.75px' }}>
          Learning Outcomes
        </Typography>
        <Typography sx={{ fontWeight: 400, fontSize: { xs: '0.75rem', md: '0.875rem' }, color: 'black' }}>
          Define outcomes used during event creation. Map each one to NACE competencies for career-aligned reporting.
        </Typography>
      </Box>

      {learningOutcomes.length === 0 && !isLoading ? (
        <Box mt={30} display="flex-col" justifyContent="center" alignItems="center">
          <Typography sx={{ fontWeight: 400, fontSize: '1rem' }}>
            No learning outcomes found
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            mt={30}
            gap={8}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setEditingOutcome(null);
              setIsManageLearningOutcomesModalOpen(true);
            }}
          >
            <AddIcon />
            Add Learning Outcome
          </Box>
        </Box>
      ) : (
        <Box mt={24}>
          <Box display="flex" flexDirection="column" gap={32}>
            {learningOutcomes.map(learningOutcome => (
              <Box
                key={learningOutcome.id}
                sx={{
                  border: '1px solid #dedede',
                  backgroundColor: 'white',
                  p: 16,
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 10,
                }}
              >
                <Box flex={1} display="flex" flexDirection="column" gap={16}>
                  <Box display="flex" flexDirection="column" gap={8}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        color: '#383838',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {learningOutcome.title}
                    </Typography>
                    <Box>
                      <Typography
                        sx={{
                          color: '#383838',
                          fontSize: { xs: '0.75rem', md: '0.875rem' },
                          lineHeight: '21px',
                        }}
                      >
                        {learningOutcome.isExpanded
                          ? learningOutcome.description
                          : truncateText(learningOutcome.description)}
                      </Typography>
                      {learningOutcome.description.length > 150 && (
                        <Typography
                          onClick={() => toggleDescription(learningOutcome.id)}
                          sx={{
                            color: theme.palette.primary.main,
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            mt: 4,
                          }}
                        >
                          {learningOutcome.isExpanded ? 'Read less' : 'Read more'}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {learningOutcome.naceCompetencies && learningOutcome.naceCompetencies.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={8} alignItems="center">
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.6875rem', md: '0.75rem' },
                          color: '#383838',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        NACE Competencies
                      </Typography>
                      {learningOutcome.naceCompetencies.map(competency => (
                        <Box
                          key={competency}
                          sx={{
                            border: '1px solid #383838',
                            borderRadius: '9999px',
                            px: 16,
                            py: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '0.6875rem', md: '0.75rem' },
                              color: '#383838',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {competency}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box
                  display="flex"
                  alignItems="center"
                  gap={16}
                  sx={{
                    alignSelf: { xs: 'flex-start', md: 'center' },
                  }}
                >
                  <Box
                    onClick={() => handleEditLearningOutcome(learningOutcome)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      height: 40,
                      px: 16,
                      border: '1px solid #dedede',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      '&:hover': { borderColor: '#383838' },
                    }}
                  >
                    <EditSquareIcon sx={{ fontSize: 19, color: '#383838' }} />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#383838',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Edit
                    </Typography>
                  </Box>
                  <Box
                    onClick={() => handleDeleteLearningOutcome(learningOutcome)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 40,
                      px: 16,
                      border: '1px solid #dedede',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      '&:hover': { borderColor: '#9b0000' },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 19, color: '#9b0000' }} />
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: '#9b0000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Remove
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={32}
            mt={24}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={8}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setEditingOutcome(null);
                setIsManageLearningOutcomesModalOpen(true);
              }}
            >
              <AddIcon />
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#23305f' }}>
                Add Learning Outcome
              </Typography>
            </Box>

            {hasMore && (
              <Box>
                <Button
                  variant="default"
                  onClick={loadMore}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {isManageLearningOutcomesModalOpen && (
        <ManageLearningOutcomesModal
          schoolId={schoolId}
          open={isManageLearningOutcomesModalOpen}
          learningOutcome={editingOutcome || undefined}
          onClose={() => {
            setIsManageLearningOutcomesModalOpen(false);
            setEditingOutcome(null);
            setPage(1);
            setHasMore(true);
            void getLearningOutcomesList(1);
          }}
        />
      )}

      <ConfirmationModal
        title="Archive learning outcome"
        open={showDeleteConfirmation}
        onCancel={() => {
          setShowDeleteConfirmation(false);
          setLearningOutcomeToDelete(null);
        }}
        onConfirm={confirmDeleteLearningOutcome}
        message={`Archive the learning outcome "${
          learningOutcomeToDelete?.title || 'Unknown'
        }"?`}
        body={
          <Typography fontSize={pxToEm(14)} color="text.secondary" mt={16}>
            It will no longer be available when creating or editing events, but
            events that already use it keep showing it (marked as archived) and
            it stays in co-curricular analytics and transcripts for historical
            tracking.
          </Typography>
        }
        confirmAction={isDeleting ? 'Archiving...' : 'Archive'}
        cancelAction="Cancel"
      />
    </Box>
  );
};
